import { useCallback, useRef, useState } from 'react';

import type { ZipcodeSearchResponse } from '../types/zipcode';

/**
 * 郵便番号検索で得た住所（BillingAddressForm のフィールドに対応）
 */
export interface ZipcodeAddressResult {
  addressPrefectureId: string;
  addressCity: string;
  addressDetail: string;
}

interface PrefectureOption {
  value: string;
  label: string;
}

const ZIPCLOUD_API_URL = 'https://zipcloud.ibsnet.co.jp/api/search';

interface UseZipcodeSearchReturn {
  /** 郵便番号が数字7桁のときのみ zipcloud を検索し、結果を onResult に渡す */
  search: (
    zipcode: string,
    onResult: (result: ZipcodeAddressResult) => void,
  ) => Promise<void>;
  isLoading: boolean;
  hasError: boolean;
}

/**
 * zipcloud 郵便番号検索 hook
 *
 * - 数字7桁のときのみ検索（Flutter 側 hk_form_state の挙動を踏襲）
 * - 同一郵便番号の連続検索はスキップ
 * - 都道府県名はマスタ（prefectureOptions）のラベル一致で ID へ変換
 *   （home_doctor_front useAddressUtil の zipcodeSearch と同じマッピング）
 * - 検索失敗時は hasError を立てるのみで、手入力を妨げない
 */
export const useZipcodeSearch = (
  prefectureOptions: PrefectureOption[],
): UseZipcodeSearchReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const lastSearchedZipcode = useRef<string | null>(null);
  // 検索の世代番号。古い検索の結果が後着したときに最新入力を上書きしないためのガード
  const searchGeneration = useRef(0);

  const search = useCallback(
    async (
      zipcode: string,
      onResult: (result: ZipcodeAddressResult) => void,
    ): Promise<void> => {
      if (!/^\d{7}$/.test(zipcode)) return;
      if (zipcode === lastSearchedZipcode.current) return;
      lastSearchedZipcode.current = zipcode;
      const generation = ++searchGeneration.current;

      setIsLoading(true);
      setHasError(false);
      try {
        const response = await fetch(
          `${ZIPCLOUD_API_URL}?zipcode=${encodeURIComponent(zipcode)}`,
        );
        if (!response.ok) {
          throw new Error(`zipcloud request failed: ${response.status}`);
        }
        const data: ZipcodeSearchResponse = await response.json();
        // 検索中に新しい検索が始まっていたら、この結果は破棄する（後着上書き防止）
        if (generation !== searchGeneration.current) return;
        if (data.results && data.results.length > 0) {
          const first = data.results[0];
          const prefecture = prefectureOptions.find(
            (option) => option.label === first.address1,
          );
          onResult({
            addressPrefectureId: prefecture?.value ?? '',
            addressCity: first.address2,
            addressDetail: first.address3,
          });
        }
        // results が null（該当なし）の場合は何もしない（Flutter 側の挙動を踏襲）
      } catch {
        if (generation !== searchGeneration.current) return;
        // 失敗時はリトライできるよう検索済み記録を破棄する
        lastSearchedZipcode.current = null;
        setHasError(true);
      } finally {
        if (generation === searchGeneration.current) {
          setIsLoading(false);
        }
      }
    },
    [prefectureOptions],
  );

  return { search, isLoading, hasError };
};
